import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { useParams } from "react-router";

import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { CumulRole } from "@app/enum/CumulRole";
import { CumulDashboardViewMode } from "@app/scene/reports/enums/CumulDashboardViewMode";
import isDashboardPinned from "@app/scene/reports/utils/isDashboardPinned";
import showDashboardDownloadInfo from "@app/scene/reports/utils/showDashboardDownloadInfo";
import { getCumulRole, getPinnedDashboardIds } from "@app/store/iam";
import { togglePinnedDashboard } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import ShowDashboard, { LuzmoDashboardRef } from "../Dashboard";
import DashboardHeader from "../DashboardHeader";
import ViewDashboard from "../ViewDashboard";

import styles from "./PinnedDashboard.module.scss";

interface Props {
  cumulRole?: CumulRole;
  onTogglePinnedDashboard: typeof togglePinnedDashboard.request;
  pinnedDashboards: CumulDashboard["id"][];
}

const PinnedDashboard: React.FC<Props> = ({
  cumulRole,
  onTogglePinnedDashboard,
  pinnedDashboards,
}) => {
  const analytics = useExtendedAnalytics(["Cumul", "Single Dashboard"]);
  useEffect(() => {
    analytics.clicked([]);
  }, [analytics]);

  const intl = useIntl();
  const dashboardRef = useRef<LuzmoDashboardRef>(null);
  const [viewMode, setViewMode] = useState(CumulDashboardViewMode.VIEW);
  const { id: dashboardId } = useParams<{ id: string }>();
  const [isLoading, startLoading, stopLoading] = useBoolean(false);

  const [activeFullView, setActiveFullView] = useState(false);

  const handleClose = useCallback(() => {
    setViewMode(CumulDashboardViewMode.VIEW);
    setActiveFullView(false);
    dashboardRef.current?.reloadDashboard();
  }, [setActiveFullView, setViewMode]);

  const handleViewMode = useCallback(
    (mode: CumulDashboardViewMode) => {
      if (
        mode === CumulDashboardViewMode.EDIT_FULL ||
        mode === CumulDashboardViewMode.EDIT_LIMITED
      ) {
        analytics.clicked(["Edit Dashboard"]);
      }
      setViewMode(mode);
      setActiveFullView(true);
    },
    [analytics, setViewMode, setActiveFullView]
  );

  const hasEditAccess = cumulRole === CumulRole.OWNER || cumulRole === CumulRole.DESIGNER;

  const handleDashboardExport = useCallback(() => {
    analytics.clicked(["Export"]);
    startLoading();
    showDashboardDownloadInfo(intl);
    dashboardRef.current?.exportDashboard().finally(() => {
      stopLoading();
    });
  }, [analytics, startLoading, stopLoading, intl]);

  const handleFullScreen = useCallback(() => {
    analytics.clicked(["Full Screen"]);
    setViewMode(CumulDashboardViewMode.VIEW);
    setActiveFullView(true);
  }, [analytics, setViewMode, setActiveFullView]);

  const handleTogglePinnedDashboard = useCallback(() => {
    if (!dashboardId) {
      return;
    }
    const pinned = isDashboardPinned(pinnedDashboards, dashboardId);
    analytics.clicked([pinned ? "Unpinned to Menu" : "Pinned to Menu"], { id: dashboardId });
    onTogglePinnedDashboard(dashboardId);
  }, [analytics, dashboardId, onTogglePinnedDashboard, pinnedDashboards]);

  const isSelectedDashboardPinned = useMemo(() => {
    if (dashboardId) {
      return isDashboardPinned(pinnedDashboards, dashboardId);
    } else {
      return false;
    }
  }, [dashboardId, pinnedDashboards]);

  return (
    <>
      <div className={styles.container}>
        <DashboardHeader
          dashboardId={dashboardId}
          hasEditAccess={hasEditAccess}
          isDashboardPinned={isSelectedDashboardPinned}
          isExportRunning={isLoading}
          onChangeMode={handleViewMode}
          onExport={handleDashboardExport}
          onFullScreen={handleFullScreen}
          onTogglePinedDashboard={handleTogglePinnedDashboard}
          showFullScreenOption={!activeFullView}
          viewMode={viewMode}
        />
        <div className={styles.dashboard}>
          {dashboardId && (
            <ShowDashboard dashboardId={dashboardId} ref={dashboardRef} viewMode={viewMode} />
          )}
        </div>
      </div>
      {dashboardId && activeFullView && (
        <ViewDashboard
          dashboardId={dashboardId}
          hasEditAccess={hasEditAccess}
          isDashboardPinned={isSelectedDashboardPinned}
          isExportRunning={isLoading}
          onChangeMode={handleViewMode}
          onExport={handleDashboardExport}
          onHide={handleClose}
          onTogglePinnedDashboard={handleTogglePinnedDashboard}
          viewMode={viewMode}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  cumulRole: getCumulRole(state),
  pinnedDashboards: getPinnedDashboardIds(state),
});

const mapDispatchToProps = {
  onTogglePinnedDashboard: togglePinnedDashboard.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinnedDashboard);
